import {
  action$,
  extractPayload,
  ofType,
  persistentReducedStream,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import { setLoadedScenarioData, updateLoadedScenarioData } from 'scope/actions';
import {
  PayloadLoadedScenarioData,
  PayloadUpdateLoadedScenarioData,
} from '@ardoq/data-model';
import { referenceListToGraphData } from 'modelInterface/util';
import { APIScopeData, ArdoqId } from '@ardoq/api-types';
import { toId, toIdSet } from '@ardoq/core';
import { componentsAdded$ } from 'streams/components/components$';
import { uniq } from 'lodash';
import { GraphModelShape } from '@ardoq/data-model';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map, withLatestFrom } from 'rxjs/operators';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';
import { scenarios$ } from 'streams/scenarios/scenarios$';
import { locale$ } from 'streams/locale$';
import type { Locale } from '@ardoq/locale';

export type LoadedScenarioData = PayloadLoadedScenarioData & {
  scenarioRelatedGraph: GraphModelShape | null;
  contextComponents: Set<ArdoqId>;
  scenarioName?: string;
};
export const EMPTY_LOADED_SCENARIO_DATA: LoadedScenarioData = {
  scenarioData: null,
  scenarioRelatedGraph: null,
  contextComponents: new Set(),
  scenarioName: undefined,
};

const activeScenarioName$ = combineLatest([activeScenario$, scenarios$]).pipe(
  map(([{ scenarioId }, scenarios]) =>
    scenarioId ? scenarios.byId[scenarioId]?.name : undefined
  ),
  distinctUntilChanged()
);

const getContextComponentsSet = (scenarioData: APIScopeData | null) => {
  if (!scenarioData) {
    return new Set<ArdoqId>();
  }

  const { components, scopeComponents } = scenarioData;
  const scopeComponentsSet = new Set(scopeComponents);
  return new Set<ArdoqId>(
    components.map(toId).filter(id => !scopeComponentsSet.has(id))
  );
};

const handleSetLoadedScenarioData = (
  { scenarioName }: LoadedScenarioData,
  [{ scenarioData }, locale]: [PayloadLoadedScenarioData, Locale]
): LoadedScenarioData => {
  return {
    scenarioData,
    scenarioRelatedGraph: scenarioData
      ? referenceListToGraphData(
          scenarioData.connected.references,
          scenarioData.connected.models,
          locale
        )
      : null,
    contextComponents: getContextComponentsSet(scenarioData),
    scenarioName,
  };
};

const handleUpdateLoadedScenarioData = (
  { scenarioData, scenarioRelatedGraph, scenarioName }: LoadedScenarioData,
  { components, references }: PayloadUpdateLoadedScenarioData
): LoadedScenarioData => {
  if (scenarioData === null) {
    return {
      scenarioData,
      scenarioRelatedGraph,
      contextComponents: getContextComponentsSet(scenarioData),
      scenarioName,
    };
  }

  // Only remove connected entities that are only in mainline context, i.e. present
  // in `connected` but not part of the scenario's own scope
  const scenarioComponentIds = toIdSet(scenarioData.components);
  const scenarioRefIds = toIdSet(scenarioData.references);

  const contextComponentIds = new Set(
    scenarioData.connected.components
      .map(toId)
      .filter(id => !scenarioComponentIds.has(id))
  );
  const contextRefIds = new Set(
    scenarioData.connected.references
      .map(toId)
      .filter(id => !scenarioRefIds.has(id))
  );

  const updatedScenarioData = {
    ...scenarioData,
    components: components.filter(comp => !contextComponentIds.has(comp._id)),
    references: references.filter(ref => !contextRefIds.has(ref._id)),
  };

  return {
    scenarioData: updatedScenarioData,
    scenarioRelatedGraph,
    contextComponents: getContextComponentsSet(updatedScenarioData),
    scenarioName,
  };
};

const handleComponentsAdded = (
  loadedScenarioData: LoadedScenarioData,
  componentIds: ArdoqId[]
): LoadedScenarioData => {
  const { scenarioData, scenarioRelatedGraph, scenarioName } =
    loadedScenarioData;
  if (scenarioData === null) {
    return loadedScenarioData;
  }

  const updatedScenarioData = {
    ...scenarioData,
    scopeComponents: uniq([...scenarioData.scopeComponents, ...componentIds]),
  };

  return {
    scenarioData: updatedScenarioData,
    scenarioRelatedGraph,
    contextComponents: getContextComponentsSet(updatedScenarioData),
    scenarioName,
  };
};

const handleActiveScenarioName = (
  loadedScenarioData: LoadedScenarioData,
  scenarioName: string | undefined
): LoadedScenarioData => ({
  ...loadedScenarioData,
  scenarioName,
});

export const defaultState = (): LoadedScenarioData => ({
  scenarioData: null,
  scenarioRelatedGraph: null,
  contextComponents: new Set(),
  scenarioName: undefined,
});

const setLoadedScenarioDataWithLocale$ = action$.pipe(
  ofType(setLoadedScenarioData),
  extractPayload(),
  withLatestFrom(locale$)
);

const reducers = [
  streamReducer(setLoadedScenarioDataWithLocale$, handleSetLoadedScenarioData),
  reducer(updateLoadedScenarioData, handleUpdateLoadedScenarioData),
  streamReducer(componentsAdded$, handleComponentsAdded),
  streamReducer(activeScenarioName$, handleActiveScenarioName),
];

export const loadedScenarioData$ = persistentReducedStream<LoadedScenarioData>(
  'loadedScenarioData$',
  defaultState(),
  reducers
);
